import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, Plane, LogOut, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY } from "@/data/content";
import { BrandWord } from "@/components/BrandWord";

export function Navbar({ variant = "landing" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else navigate("/#" + id);
  };

  const links = [
    { label: "Services", id: "services" },
    { label: "Gallery", id: "gallery" },
    { label: "About", id: "about" },
    { label: "Contact", id: "contact" },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-brand-navy/70 border-b border-white/10">
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <span className="w-9 h-9 rounded-full bg-brand-gold/15 border border-brand-gold/40 flex items-center justify-center">
            <Plane className="w-4 h-4 text-brand-gold" />
          </span>
          <BrandWord className="text-lg tracking-tight leading-none" />
        </Link>

        {variant === "landing" && (
          <div className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="text-sm text-white/70 hover:text-brand-gold transition-colors"
              >
                {l.label}
              </button>
            ))}
          </div>
        )}

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <Button
                onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard")}
                variant="ghost"
                className="text-white/80 hover:text-brand-gold hover:bg-white/5"
                data-testid="nav-dashboard-btn"
              >
                <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
              </Button>
              <Button
                onClick={async () => { await logout(); navigate("/"); }}
                variant="outline"
                className="border-brand-gold/40 text-brand-gold hover:bg-brand-gold hover:text-brand-navy"
                data-testid="nav-logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => navigate("/login")} variant="ghost" className="text-white/80 hover:text-brand-gold hover:bg-white/5" data-testid="nav-login-btn">
                Sign In
              </Button>
              <Button onClick={() => navigate("/register")} className="rounded-full bg-brand-gold text-brand-navy hover:bg-brand-goldhover font-semibold" data-testid="nav-register-btn">
                Get Started
              </Button>
            </>
          )}
        </div>

        <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-brand-navy px-6 py-4 space-y-3" data-testid="nav-mobile-menu">
          {variant === "landing" && links.map((l) => (
            <button key={l.id} onClick={() => scrollTo(l.id)} className="block w-full text-left text-white/80 py-2">
              {l.label}
            </button>
          ))}
          {user ? (
            <>
              <Button onClick={() => { setOpen(false); navigate(user.role === "admin" ? "/admin" : "/dashboard"); }} className="w-full bg-brand-gold text-brand-navy">Dashboard</Button>
              <Button onClick={async () => { await logout(); navigate("/"); }} variant="outline" className="w-full border-brand-gold/40 text-brand-gold">Sign Out</Button>
            </>
          ) : (
            <>
              <Button onClick={() => { setOpen(false); navigate("/login"); }} variant="outline" className="w-full border-brand-gold/40 text-brand-gold">Sign In</Button>
              <Button onClick={() => { setOpen(false); navigate("/register"); }} className="w-full bg-brand-gold text-brand-navy">Get Started</Button>
            </>
          )}
        </div>
      )}
    </header>
  );
}
